import * as React from 'react';
import { stringify } from 'qs';
import { lazy, Suspense, useState, useEffect } from 'react';
import Config from 'config';
import { useMountEffect } from '@react-hookz/web/esm';
import { useUpdateEffect } from 'ahooks';
import {
  TComponentSubmitValues,
  TComponentValidatorObj,
} from '../../../../types';
import IProperty from '../../../../JSApi/constants/IProperty';
import { setFieldFocusFromJSApi } from '../../../../store/JSApiSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { TRequestError } from '../../../../store/types';
import {
  formatMessage,
  getOutOfSyncFields,
  isArrayOfStrings,
  isEmptyObj,
  obj2Arr,
} from '../../../../utils/util';
import {
  updateActionState,
  updateActionEvent,
  setGotoState,
  updateInvalidValues,
  setActionFormSubmitState,
  TActionState,
} from '../../../../store/appStateSlice';
import {
  IValuesSlice,
  processAllOutOFSyncFieldsSubmit,
  TApiaFieldValuesObj,
  TProcessSubmitAllPayload,
} from '../../../../store/valuesSlice';
import store from '../../../../store/index';

import {
  TCheckWebDavDocsLocksResponse,
  fetchWebdavLocks,
} from '../../../../store/checkWebDavDocsLocksSlice';

import {
  fetchHasSignableFrms,
  THasSignableForms,
} from '../../../../store/hasSignableFormsSlice';

import {
  fetchConfirm,
  TConfirmFormResponse,
  fetchTaskAction,
  TFetchTaskActionResponse,
  TFetchConfirmParams,
  confirmFormResult,
  setConfirmSubmitSucessMsg,
} from '../../../../store/confirmFormSlice';

import { TFetchConfirmPathResponse } from '../../../../store/confirmTaskPathSlice';
import DocumentsModal from './modals/DocumentsModal';
import {
  fetchIfWizard,
  TCheckWizardResponse,
} from '../../../../store/checkWizardSlice';
import { ApiaActions, makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import getAction from '../../../common/actions/getAction';
import SignFormModal, { IEncodedSignData } from '../digitalSign/SignFormModal';
import useValidation from '../../../common/previousValidation/useValidation';
import fieldsStore from '../fieldsStore/fieldsStore';
import ScreenLockSuspense from '../../../common/dialogs/ScreenLockSuspense';
import formsEventsController from '../formsEvents/formsEventsController';
import {
  forceScreenLock,
  releaseScreenLock,
} from '../../../common/dialogs/modals/ScreenLock';
import focus from '../../../../classes/focusController';
import { ConfirmModal } from '../../../common';
import { hasSucceedValidation } from '../../../../store/previousValidationSlice';
import {
  dispatchNotifications,
  notify,
  TNotificationMessage,
} from '../../../common/notifications/util';
import { setHasChangedAnyField } from '../fieldsStore/MakeField';
import { getCurrentTabsString } from '../../../../config/prod';
import { reloadCurrentForm } from '../../../../store/fieldEventsSlice';
import { fetchAjaxEvents } from '../fieldsStore/hooks/useServerEvents';
import { ApiaUtil } from '@apia/components';
import { customEvents } from '../../../../utils/dom';

const ApiaExecGroupsModal = lazy(
  () =>
    import(
      /* webpackChunkName: "apia-execution-groupsModal" */ './modals/ApiaExecGroupsModal'
    ),
);
const ApiaPathSelectionModal = lazy(
  () =>
    import(
      /* webpackChunkName: "apia-execution-pathSelectionModal" */ './modals/ApiaPathSelectionModal'
    ),
);

const formHasCaptcha = (): boolean => {
  const valueState = store?.getState()?.valuesSlice;
  const valueStateStore = obj2Arr(valueState);
  return (
    valueStateStore.filter((field) => {
      if (field?.valueState?.fieldType === IProperty.TYPE_CAPTCHA) {
        return field;
      }
      return null;
    }).length !== 0
  );
};

const getCaptchaOBJ = (): ({
  [x: string]: TComponentSubmitValues;
} | null)[] => {
  const valueState = store?.getState()?.valuesSlice;
  const valueStateStore = obj2Arr(valueState);

  return valueStateStore
    .filter((field) => {
      if (field?.valueState?.fieldType === IProperty.TYPE_CAPTCHA) {
        return field;
      }
      return null;
    })
    .map((ele) => {
      if (ele && ele.id) {
        const idArr = ele.id?.split('_');
        return {
          [`${Config.TAB_ID}${idArr[0]}_${idArr[1]}`]: ele.valueState?.value,
        };
      }
      return null;
    });
};

const updateInvalidFieldsState = (): string[] => {
  const valueState = store?.getState()?.valuesSlice;
  const requiredDomElements = document.body.querySelectorAll(
    '[required], [data-required]',
  );

  // grab requireds via dom as they come in x/y order by defualt including ones
  // inside grid
  const invalidValuesIds = Array.from(requiredDomElements)
    .filter((fieldDOM: Element) => {
      let id;
      if (fieldDOM instanceof HTMLInputElement) {
        const { type } = fieldDOM;
        id =
          type === IProperty.TYPE_RADIO
            ? fieldDOM.name
            : fieldDOM.id || fieldDOM.dataset?.fieldId;
      } else if (fieldDOM instanceof HTMLElement) {
        id = fieldDOM.id || fieldDOM.dataset?.fieldId;
      }
      if (id) {
        const filedValueState = valueState[id] as IValuesSlice;
        const v = filedValueState?.valueState as TComponentValidatorObj;
        if (v?.isValid === false) return fieldDOM;
      }
      return null;
    })
    .map((fieldDOM) => {
      if (fieldDOM instanceof HTMLElement) {
        const id = fieldDOM.id || fieldDOM?.dataset?.fieldId;
        return id;
      }
      return null;
    });

  if (isArrayOfStrings(invalidValuesIds) && invalidValuesIds !== undefined) {
    store.dispatch(updateInvalidValues(invalidValuesIds as string[]));
    return invalidValuesIds as string[];
  }
  return [];
};

const isFormValid = (): boolean => {
  const invalidValues = updateInvalidFieldsState();
  return invalidValues.length === 0;
};

const getFirstInvalidField = (): string => {
  const invalidFieldList: string[] =
    store?.getState().appStateSlice.invalidValues;
  return invalidFieldList[0];
};

export interface IApiaPage {
  children: React.ReactNode;
}

const ApiaPage = ({ children }: IApiaPage): JSX.Element => {
  const dispatch = useAppDispatch();

  const actionState = useAppSelector(
    (state) => state.appStateSlice.actionState,
  );

  const actionEvent = useAppSelector(
    (state) => state.appStateSlice.actionEvent,
  );

  const groupSelectionObj = useAppSelector(
    (state) => state.confirmTaskPathSlice.groupSelectionObj,
  );

  const [confirmObj, setConfirmObject] = useState<
    TConfirmFormResponse | TFetchConfirmPathResponse | null
  >(null);

  const [modalToShow, setModalToShow] = useState<
    'pathSelection' | 'groupSelection' | ''
  >('');

  const [showSaveTaskModal, setShowSaveTaskModal] = useState<{
    title?: string;
    text?: string;
    exit?:
      | 'confirmOkOnCloseSplash'
      | 'confirmOkOnClose'
      | 'confirmOkOnSaveSplash'
      | null;
    show: boolean;
  }>({
    exit: null,
    show: false,
  });

  const [showReleaseTaskModal, setShowReleaseTaskModal] = useState<{
    title?: string;
    text?: string;
    exit?: 'releaseOkOnClose' | null;
    show: boolean;
  }>({ exit: null, show: false });

  const validateAction = useValidation();

  useUpdateEffect(() => {
    async function validate() {
      if (
        actionState &&
        (actionState === Config.FRM_ACTION_NEXT ||
          actionState === Config.FRM_ACTION_CONFIRM)
      ) {
        const isValid = await validateAction();
        if (hasSucceedValidation(isValid)) {
          updateActionEvent('form-on-validation');
        } else {
          updateActionEvent('form-on-error');
          updateActionState(null);
        }
      }
    }
    void (async function checkFieldsStatus() {
      const validateActions: TActionState[] = ['btnConfirm', 'btnNext'];
      if (validateActions.includes(actionState)) {
        const fieldStoreResult = await fieldsStore.validate();
        if (fieldStoreResult === true) {
          void validate();
          return;
        }
        if (typeof fieldStoreResult === 'string') {
          notify({ type: 'warning', message: fieldStoreResult });
        }
        updateActionState(null);
      }
    })();
    if (actionState === Config.FRM_ACTION_PREVIOUS) {
      if (focus.notifications()) {
        updateActionState(null);
      } else dispatch(setGotoState(`${Config.FORM_GOTO_PREVIOUS_STEP_URL()}`));
    }
  }, [actionState]);

  const fetchConfirmData = React.useCallback(
    async function fetchConfirmData(
      params: TApiaFieldValuesObj[],
    ): Promise<TConfirmFormResponse | TRequestError> {
      dispatch(setActionFormSubmitState(null));
      const resultFetchConfirm = await dispatch(fetchConfirm(params));
      if (fetchConfirm.fulfilled.match(resultFetchConfirm)) {
        return resultFetchConfirm.payload as TConfirmFormResponse;
      }

      return resultFetchConfirm.payload as TRequestError;
    },
    [dispatch],
  );

  const confirm = React.useCallback(() => {
    // fetch From Confirm response
    let captchaParams: TApiaFieldValuesObj[] = [];
    if (formHasCaptcha()) {
      captchaParams = getCaptchaOBJ() as TApiaFieldValuesObj[];
    }
    const fetchConfirmResponse = fetchConfirmData(captchaParams);
    fetchConfirmResponse
      .then((data) => {
        const confirmResponse = data as TConfirmFormResponse;
        // path selection modal
        if (confirmResponse.pathSelection === true) {
          // Dialog Modal Path Selection
          setConfirmObject(confirmResponse);
          setModalToShow('pathSelection');
          updateActionEvent(null);
        } else if (confirmResponse.groupSelection === true) {
          // Dialog Modal Select Groups
          setConfirmObject(confirmResponse);
          setModalToShow('groupSelection');
          updateActionEvent(null);
          return null;
        }
        if (confirmResponse.actions?.action.param[1] === 'confirmOkOnClose') {
          // check if wizard and navigate
          dispatch(fetchIfWizard())
            .then((wizResponseData) => {
              const wizardResponse = wizResponseData as TCheckWizardResponse;
              const payload = wizardResponse?.payload;
              if (payload) {
                const responseSize = payload ? Object.keys(payload).length : 0;
                setHasChangedAnyField(false);
                if (payload?.url !== null && payload?.url !== undefined) {
                  dispatch(setGotoState(`${window.CONTEXT}/${payload.url}`));
                }
                if (responseSize === 0) {
                  const { location } = window.parent;
                  if (Config.ON_FINISH === '1') {
                    window?.parent?.parent?.close();
                    store.dispatch(
                      setConfirmSubmitSucessMsg(window.LBL_COMPLETE_OPERATION),
                    );
                  } else if (
                    (window.frameElement as HTMLIFrameElement)?.src.match(
                      /frompanel/i,
                    )
                  ) {
                    (window.frameElement as HTMLIFrameElement).src = (
                      window.frameElement as HTMLIFrameElement
                    ).src;
                  } else if (Config.ON_FINISH === '2') {
                    location.reload();
                  } else if (Config.ON_FINISH === '5') {
                    location.href = Config.ON_FINISH_URL;
                  } else {
                    let tabContainer =
                      window.parent.document.getElementById('tabContainer');
                    if (tabContainer == null) {
                      tabContainer =
                        window.top!.document.getElementById('tabContainer');
                    }

                    if (tabContainer) {
                      (tabContainer as any).removeActiveTab(true);
                    } else {
                      ApiaUtil.instance.tabs.currentTab.close(true);
                    }
                  }
                }
              }
            })
            .catch((err) => console.warn(err));
        }
        // confirm success
        if (
          confirmResponse.load?.text &&
          !confirmResponse.actions?.action.param[1]
        ) {
          // Success confirm
          const textMsg = confirmResponse.load?.text.label;
          dispatch(
            confirmFormResult({
              confirmSubmitSucessMsg: textMsg,
              onClose: confirmResponse?.onClose,
              autoCloseTab: !textMsg,
            }),
          );
        }

        if (confirmResponse.sysMessages || confirmResponse.sysExceptions) {
          releaseScreenLock();
          void fetchAjaxEvents({
            ajaxUrl: makeApiaUrl({
              action: 'checkAfterConfirmAjax',
              ajaxUrl: 'apia.execution.FormAction.run',
              frmParent: 'E',
            }),
          });
          updateActionEvent('form-on-error');
        }
        return false;
      })
      .catch((err) => {
        console.warn(err);
        updateActionEvent('form-on-error');
      });
  }, [dispatch, fetchConfirmData]);

  useEffect(() => {
    async function syncOutOfSyncFields(
      outOfSyncFields: (TApiaFieldValuesObj | null)[],
    ): Promise<TProcessSubmitAllPayload | TRequestError> {
      const syncResult = await dispatch(
        processAllOutOFSyncFieldsSubmit(outOfSyncFields),
      );
      if (processAllOutOFSyncFieldsSubmit.fulfilled.match(syncResult)) {
        const result = syncResult.payload;
        if (result !== null) {
          return result;
        }
      }
      return syncResult.payload as TRequestError;
    }
    if (actionEvent === 'form-on-validation') {
      setConfirmObject(null);
      setModalToShow('');
      if (!isFormValid()) {
        // put focus on first required component empty
        const invalidRequiredFieldId = getFirstInvalidField();
        dispatch(
          setFieldFocusFromJSApi({
            focusFieldId: invalidRequiredFieldId,
          }),
        );
        updateActionState(null);
      } else {
        void (async function fireFormSubmitEvents() {
          forceScreenLock();
          const hasFiredCorrectly =
            await formsEventsController.fireEvents('onSubmit');
          if (hasFiredCorrectly) {
            updateActionEvent('form-on-sync-fields');
          } else {
            updateActionState(null);
            updateActionEvent(null);
            releaseScreenLock();
          }
        })();
      }
    }
    if (actionEvent === 'form-on-sync-fields') {
      const outOfSyncFields = getOutOfSyncFields();
      if (outOfSyncFields.length > 0) {
        const syncResult = syncOutOfSyncFields(outOfSyncFields);
        syncResult
          .then((syncData) => {
            const response = syncData as TProcessSubmitAllPayload['payload'];
            if (response !== null && response.success === true) {
              // all synced
              updateActionEvent('form-on-check-web-dav');
            }
          })
          .catch((err) => {
            console.warn(err);
            updateActionEvent('form-on-error');
          });
      } else {
        updateActionEvent('form-on-check-web-dav');
      }
    }
    if (actionEvent === 'form-on-force-confirm') confirm();
  }, [actionEvent, confirm, dispatch, modalToShow]);

  useEffect(() => {
    if (actionEvent === 'form-on-next') {
      if (formHasCaptcha()) {
        const captchaData = getCaptchaOBJ();
        const paramsObj = Object.assign(
          {},
          ...captchaData,
        ) as TFetchConfirmParams;
        const strCaptcha = stringify(paramsObj, {
          arrayFormat: 'repeat',
        });
        dispatch(
          setGotoState(`${Config.FORM_GOTO_NEXT_STEP_URL()}&${strCaptcha}`),
        );
      } else {
        dispatch(setGotoState(`${Config.FORM_GOTO_NEXT_STEP_URL()}`));
      }
    }
  }, [actionEvent, dispatch]);

  const [signFormData, setSignFormData] = React.useState<
    IEncodedSignData | undefined
  >(undefined);

  useUpdateEffect(() => {
    async function fetchWebDavLocksData(): Promise<
      TCheckWebDavDocsLocksResponse | TRequestError
    > {
      const resultFetchWebDav = await dispatch(fetchWebdavLocks(null));
      if (fetchWebdavLocks.fulfilled.match(resultFetchWebDav)) {
        return resultFetchWebDav.payload;
      }
      return resultFetchWebDav.payload as TRequestError;
    }

    async function fetchHasSignableForms() {
      const resultHasSignableFrms = await dispatch(fetchHasSignableFrms());
      return resultHasSignableFrms;
    }

    if (actionEvent === 'form-on-check-web-dav') {
      const webDavResponse = fetchWebDavLocksData();
      webDavResponse
        .then((data) => {
          const response = data as TCheckWebDavDocsLocksResponse;
          if (response?.actions) {
            const responseWebDavAction = response?.actions?.action;
            const toDo = responseWebDavAction?.toDo;
            const param = responseWebDavAction?.param;
            if (toDo === 'functionTimedCall') {
              switch (param[1]) {
                case 'successWebDavLockFunction': {
                  const hasSignableFrm = fetchHasSignableForms();
                  hasSignableFrm
                    .then((singableFormsData) => {
                      const hasFrms =
                        singableFormsData.payload as THasSignableForms;

                      if (isEmptyObj(hasFrms) || hasFrms === null) {
                        if (actionState !== 'btnNext') {
                          confirm();
                        } else updateActionEvent('form-on-next');
                      } else if (
                        hasFrms?.sysMessages ||
                        hasFrms?.sysException
                      ) {
                        dispatchNotifications(hasFrms as TNotificationMessage);
                        updateActionEvent('form-on-error');
                      }
                      if (!isEmptyObj(hasFrms) && hasFrms !== null) {
                        setSignFormData(hasFrms as IEncodedSignData);
                      }
                    })
                    .catch((err) => {
                      console.warn(err);
                      updateActionEvent('form-on-error');
                    });
                  break;
                }
                default:
                  break;
              }
            }
          } else if (response.sysMessages || response.sysException) {
            dispatchNotifications(response as TNotificationMessage);
            updateActionEvent('form-on-error');
          }
        })
        .catch((err) => {
          console.warn(err);
          updateActionEvent('form-on-error');
        });
    }
  }, [actionEvent]);

  // Save Task Action
  useEffect(() => {
    if (actionState === Config.FRM_ACTION_SAVE) {
      // saveTask Action request
      dispatch(fetchTaskAction(Config.SAVE_TASK_URL()))
        .then((data) => {
          const saveResponse = data.payload as TFetchTaskActionResponse;
          const showSaveConfirmMessage = saveResponse?.onClose as string;
          if (
            showSaveConfirmMessage === 'confirmOkOnCloseSplash' ||
            showSaveConfirmMessage === 'confirmOkOnClose'
          ) {
            setShowSaveTaskModal({
              show: true,
              exit: showSaveConfirmMessage,
              title: saveResponse?.load?.text?.title,
              text: saveResponse?.load?.text?.label,
            });
          } else if (
            saveResponse?.onClose === 'confirmOkOnSaveSplash' ||
            saveResponse?.actions?.action.param[1] === 'confirmOkOnSaveSplash'
          ) {
            ApiaUtil.instance.tabs.currentTab.close();

            return;
          }

          if (saveResponse.sysMessages || saveResponse.sysExceptions) {
            updateActionEvent('form-on-error');
          }
        })
        .catch((err) => {
          console.warn(err);
          updateActionEvent('form-on-error');
        });
      updateActionState(null);
    }
  }, [dispatch, actionState]);

  const onSaveTaskConfirmOk = () => {
    if (showSaveTaskModal.exit === 'confirmOkOnCloseSplash') {
      ApiaUtil.instance.tabs.currentTab.close(true);
    } else if (showSaveTaskModal.exit === 'confirmOkOnClose') {
      setShowSaveTaskModal({ show: false });
      store.dispatch(
        reloadCurrentForm(
          makeApiaUrl({
            action: 'refreshStep',
            currentTab: getCurrentTabsString(),
          }),
        ),
      );
    }
    updateActionState(null);
  };

  // Release Task Action
  useEffect(() => {
    if (actionState === Config.FRM_ACTION_FREE) {
      // saveTask Action request
      dispatch(fetchTaskAction(Config.RELEASE_TASK_URL()))
        .then((data) => {
          const responseReleaseTask = data.payload as TFetchTaskActionResponse;
          if (responseReleaseTask?.onClose === 'releaseOkOnClose') {
            setShowReleaseTaskModal({
              show: true,
              title: responseReleaseTask.load?.text.title,
              text: responseReleaseTask.load?.text.label,
            });
          } else if (
            responseReleaseTask.sysMessages ||
            responseReleaseTask.sysExceptions
          ) {
            updateActionEvent('form-on-error');
          }
          updateActionState(null);
        })
        .catch((err) => {
          console.warn(err);
          updateActionEvent('form-on-error');
        });
    }
  }, [dispatch, actionState]);

  const onReleaseTaskConfirmOk = () => {
    ApiaUtil.instance.tabs.currentTab.close();
  };

  useEffect(() => {
    if (groupSelectionObj) {
      setModalToShow('groupSelection');
      setConfirmObject(groupSelectionObj);
    }
  }, [groupSelectionObj]);

  useMountEffect(() => {
    window.closeTabConfirm = () => {
      return new Promise((resolve, reject) => {
        ApiaUtil.instance.dialogs
          .confirm({
            children: Config.GNR_PER_DAT_ING,
            title: formatMessage(Config.MSG_CLOSE_TSK_TIT, {
              TOK: Config.TSK_TITLE,
            }),
          })
          .then(resolve)
          .catch(reject);
      });
    };

    ApiaActions.on('action', (action) => {
      if (action.toDo === 'linkTimed') {
        if (window.frameElement?.classList?.contains('jspModal')) {
          setTimeout(() => {
            window.frameElement!.dispatchEvent(
              new CustomEvent(customEvents.iframeModalClose),
            );
          }, 1000);
        }
        if (action.param[1].match(/action=list/)) {
          getAction('execution/entities/backToList').then((func) => {
            /* Es un linkTimed de Apia, se respeta comportamiento actual */
            setTimeout(() => {
              if (func) func(window.CONTEXT + action.param[1]);
            }, Number(action.param[0]));
          }, console.error);
        }
      }
    });
  });

  return (
    <>
      {children}

      <DocumentsModal />

      {signFormData && (
        <SignFormModal
          signData={signFormData}
          onClose={() => {
            setSignFormData(undefined);
          }}
        />
      )}

      {confirmObj && modalToShow === 'groupSelection' && (
        <Suspense fallback={<ScreenLockSuspense />}>
          <ApiaExecGroupsModal
            formModalObj={confirmObj as TFetchConfirmPathResponse}
          />
        </Suspense>
      )}

      {confirmObj && modalToShow === 'pathSelection' && (
        <Suspense fallback={<ScreenLockSuspense />}>
          <ApiaPathSelectionModal
            formModalObj={confirmObj as TFetchConfirmPathResponse}
          />
        </Suspense>
      )}

      <ConfirmModal
        isOpen={showSaveTaskModal.show}
        html={showSaveTaskModal.text}
        title={showSaveTaskModal.title}
        onConfirm={onSaveTaskConfirmOk}
        size="md"
      />

      <ConfirmModal
        isOpen={showReleaseTaskModal.show}
        title={showReleaseTaskModal.title}
        html={showReleaseTaskModal.text}
        onConfirm={onReleaseTaskConfirmOk}
        size="sm"
      />
    </>
  );
};

export default ApiaPage;
